/// <amd-module name="Wit/WorkItemForm/Details/WorkItemFormExtensionHost" />
import * as React from "react";
import { ILegacyPlatformService } from "VSS/Legacy/Types";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { usePageContext, WrappedComponent } from "VSS/Platform/Layout";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { ResizeOptions } from "Wit/Common/Extensions";
import { useWorkItem } from "Wit/WorkItemForm/Common/Hooks/WorkItemFormPageContext";
import { IWorkItemLoadedArgs, IWorkItemNotificationListener } from "Wit/WorkItemForm/Contracts/Contracts";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";

export interface IWorkItemFormExtensionHostProps {
    contributionId: string;
    context?: any;
    onLoaded?: (workItem: IWorkItem, hostInstance?: any) => void;
    skipRender?: boolean;
    height?: number;
}

const witFormContributionSlowLoadDuration = 30000;
const witFormContributionMaxWaitForHandshakeEvent = 20000;

export function WorkItemFormExtensionHost({ contributionId, context, onLoaded, skipRender, height }: IWorkItemFormExtensionHostProps) {
    const workItem = useWorkItem();

    const behavior = {
        slowWarningDurationMs: witFormContributionSlowLoadDuration,
        maxHandshakeDurationMs: witFormContributionMaxWaitForHandshakeEvent,
        resizeOptions: ResizeOptions.FixedWidth
    };

    return (
        <WrappedComponent
            key={contributionId}
            dependencies={["ms.vss-web.legacy-content", "ms.vss-web.legacy-vss-ui-content"]}
            wrappedType="legacy"
            wrappedProps={{
                componentType: "vssLegacyContributionContentHost",
                modules: ["VSSPreview/Providers/ContentHost"],
                wrappedProps: {
                    height: height,
                    className: "flex-grow",
                    context: context || {},
                    contribution: contributionId,
                    contributionHostBehavior: behavior,
                    onLoaded: (hostInstance: IWorkItemNotificationListener) => _onLoaded(workItem, hostInstance, onLoaded),
                    expectRegisteredInstance: true,
                    skipRender: skipRender,
                    useResizeEventHandler: true
                }
            }}
        />
    );
}

function _onLoaded(workItem: IWorkItem, hostInstance?: IWorkItemNotificationListener, onLoaded?: (workItem: IWorkItem, hostInstance?: any) => void) {
    // If the extension registered an object, check if that object has an onLoaded function.
    if (hostInstance?.onLoaded) {
        const args: IWorkItemLoadedArgs = {
            id: workItem.id,
            isNew: workItem.isNew(),
            isReadOnly: workItem.isReadOnly()
        };
        hostInstance.onLoaded(args);
    }

    if (onLoaded) {
        onLoaded(workItem, hostInstance);
    }
}

/**
 * Get a the registered instance of the given contribution.
 * Roughly analagous to old web platform _getServiceContributionWithSource() in TFS.WorkItemTracking.Extensions.ts.
 */
export async function getRegisteredInstance(
    pageContext: IVssPageContext,
    workItem: IWorkItem,
    contributionId: string,
    context: any
): Promise<IWorkItemNotificationListener | undefined> {
    try {
        const legacyService = pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");
        const [contributionControls] = await legacyService.requireModules(["VSS/Contributions/Controls"]);
        const contributionHost = await contributionControls.getBackgroundHost(contributionId);
        const listener: IWorkItemNotificationListener = await contributionHost.getRegisteredInstance(contributionId, context);
        _onLoaded(workItem, listener);
        return listener;
    } catch (error) {
        console.log(error);
        return undefined;
    }
}
